import { StyleSheet } from 'react-native'
import { Theme } from '@monorepo/relayably/theme'

export const hexToRgba = (hex: string | undefined, alpha: number) => {
  if (!hex) {
    return `rgba(255, 255, 255, ${alpha})`
  }

  if (!hex.startsWith('#')) {
    return hex
  }

  const normalized = hex.replace('#', '')
  const value = normalized.length === 3 ? normalized.split('').map(char => `${char}${char}`).join('') : normalized
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const createPaneStyles = (theme: Theme) =>
  StyleSheet.create({
    pane: {
      gap: 24,
    },
    socialGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 14,
    },
    socialButton: {
      alignItems: 'center',
      backgroundColor: theme.colors.background,
      borderColor: theme.colors.border,
      borderRadius: 12,
      borderWidth: 1,
      flexDirection: 'row',
      gap: 10,
      justifyContent: 'center',
      minWidth: 120,
      paddingHorizontal: 14,
      paddingVertical: 13,
    },
    socialButtonText: {
      color: theme.colors.text,
      fontSize: 14,
      fontWeight: '600',
    },
    divider: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: 12,
      marginVertical: 4,
    },
    dividerLine: {
      backgroundColor: theme.colors.border,
      flex: 1,
      height: 1,
    },
    dividerText: {
      color: theme.colors.inactive,
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 1.6,
      textTransform: 'uppercase',
    },
    tabs: {
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.border,
      borderRadius: 12,
      borderWidth: 1,
      flexDirection: 'row',
      padding: 4,
    },
    tab: {
      alignItems: 'center',
      borderRadius: 8,
      flex: 1,
      flexDirection: 'row',
      gap: 8,
      justifyContent: 'center',
      paddingVertical: 10,
    },
    tabActive: {
      backgroundColor: theme.colors.background,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.08,
      shadowRadius: 2,
    },
    tabText: {
      color: theme.colors.inactive,
      fontSize: 13,
      fontWeight: '600',
    },
    tabTextActive: {
      color: theme.colors.text,
    },
    form: {
      gap: 20,
    },
    fieldGroup: {
      gap: 8,
    },
    label: {
      color: theme.colors.inactive,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 1.6,
      textTransform: 'uppercase',
    },
    input: {
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.border,
      borderRadius: 12,
      borderWidth: 1,
      color: theme.colors.text,
      fontSize: 14,
      paddingHorizontal: 16,
      paddingVertical: 14,
    },
    iconInput: {
      paddingLeft: 42,
    },
    inputIconWrap: {
      justifyContent: 'center',
      position: 'relative',
    },
    inputIcon: {
      left: 14,
      position: 'absolute',
      zIndex: 2,
    },
    row: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: 12,
    },
    submitButton: {
      alignItems: 'center',
      backgroundColor: theme.colors.primary,
      borderRadius: 12,
      flexDirection: 'row',
      gap: 10,
      justifyContent: 'center',
      paddingHorizontal: 18,
      paddingVertical: 15,
    },
    submitButtonDisabled: {
      opacity: 0.65,
    },
    submitButtonText: {
      color: theme.colors.staticWhite,
      fontSize: 14,
      fontWeight: '700',
    },
    buttonDisabled: {
      opacity: 0.55,
    },
    footerText: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 20,
      textAlign: 'center',
    },
    actionLink: {
      color: theme.colors.primary,
      fontWeight: '700',
    },
    helperLink: {
      color: theme.colors.inactive,
      fontSize: 11,
      fontWeight: '600',
    },
    legalText: {
      color: theme.colors.inactive,
      fontSize: 11,
      lineHeight: 18,
      textAlign: 'center',
    },
    rememberRow: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: 10,
    },
    checkbox: {
      borderColor: theme.colors.primary,
      borderRadius: 5,
      borderWidth: 1.5,
      height: 16,
      width: 16,
    },
    rememberText: {
      color: theme.colors.inactive,
      fontSize: 12,
    },
    tertiaryCard: {
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.border,
      borderRadius: 12,
      borderWidth: 1,
      flexDirection: 'row',
      gap: 12,
      padding: 16,
    },
    tertiaryColumn: {
      flex: 1,
      gap: 4,
    },
    tertiaryTitle: {
      color: theme.colors.text,
      fontSize: 12,
      fontWeight: '700',
    },
    tertiaryBody: {
      color: theme.colors.inactive,
      fontSize: 11,
      lineHeight: 16,
    },
    navRow: {
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingHorizontal: 8,
    },
    navButton: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: 6,
    },
    navButtonText: {
      color: theme.colors.text,
      fontSize: 13,
      fontWeight: '600',
    },
    successView: {
      alignItems: 'center',
      gap: 24,
      paddingVertical: 28,
    },
    successIconWrap: {
      alignItems: 'center',
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.border,
      borderRadius: 999,
      borderWidth: 1,
      height: 64,
      justifyContent: 'center',
      width: 64,
    },
    securityHint: {
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.border,
      borderRadius: 24,
      borderWidth: 1,
      color: theme.colors.inactive,
      fontSize: 11,
      fontStyle: 'italic',
      lineHeight: 18,
      padding: 16,
      textAlign: 'center',
    },
    codeRow: {
      flexDirection: 'row',
      gap: 10,
      justifyContent: 'center',
    },
    codeInput: {
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.border,
      borderRadius: 12,
      borderWidth: 1,
      color: theme.colors.text,
      fontSize: 24,
      fontWeight: '700',
      height: 60,
      textAlign: 'center',
      width: 44,
    },
    errorText: {
      color: theme.colors.error,
      fontSize: 13,
      lineHeight: 18,
    },
  })
